/**
 * Recovery Orchestration Dashboard
 * For BCM Manager and IT-DRP Manager to orchestrate integrated BCP/IT-DRP plans
 * Features:
 * - View all BCP and IT-DRP plans
 * - Select plans based on disaster scenario
 * - Visualize recovery tasks in Swimlane/Network diagram
 * - Track parallel task execution
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import HybridCoordinationRollup from "../components/orchestration/HybridCoordinationRollup";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Play,
  Target,
  Shield,
  Server,
  Building2,
  Cloud,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Users,
  Network,
  LayoutList,
  Eye,
  Zap,
  RefreshCw,
  Download,
  Filter,
  ChevronRight,
  Activity,
  Layers,
  GitBranch,
  Sparkles,
  Loader2,
  AlertCircle,
} from "lucide-react";

// Disaster Scenarios
const DISASTER_SCENARIOS = [
  { id: "data_center_failure", name: "Data Center Failure", level: "critical", icon: Server, color: "bg-red-100 text-red-700" },
  { id: "cyber_attack", name: "Cyber Attack / Ransomware", level: "critical", icon: Shield, color: "bg-red-100 text-red-700" },
  { id: "cloud_outage", name: "Cloud Provider Outage", level: "high", icon: Cloud, color: "bg-sky-100 text-sky-700" },
  { id: "network_failure", name: "Network Infrastructure Failure", level: "high", icon: Network, color: "bg-sky-100 text-sky-700" },
  { id: "building_evacuation", name: "Building Evacuation", level: "medium", icon: Building2, color: "bg-amber-100 text-amber-700" },
  { id: "power_outage", name: "Extended Power Outage", level: "medium", icon: Zap, color: "bg-amber-100 text-amber-700" },
  { id: "application_failure", name: "Critical Application Failure", level: "medium", icon: Activity, color: "bg-amber-100 text-amber-700" },
];

// Color palette for swimlanes
const SWIMLANE_COLORS = [
  { bg: "bg-blue-100", border: "border-blue-300", text: "text-blue-700", header: "bg-blue-600" },
  { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-700", header: "bg-emerald-600" },
  { bg: "bg-purple-100", border: "border-purple-300", text: "text-purple-700", header: "bg-purple-600" },
  { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-700", header: "bg-amber-600" },
  { bg: "bg-rose-100", border: "border-rose-300", text: "text-rose-700", header: "bg-rose-600" },
  { bg: "bg-cyan-100", border: "border-cyan-300", text: "text-cyan-700", header: "bg-cyan-600" },
  { bg: "bg-sky-100", border: "border-sky-300", text: "text-sky-700", header: "bg-sky-700" },
  { bg: "bg-sky-100", border: "border-sky-300", text: "text-sky-700", header: "bg-sky-800" },
];

export default function RecoveryOrchestrationPage({ embedded = false }) {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plans");
  
  // Plans data
  const [bcpPlans, setBcpPlans] = useState([]);
  const [itdrpPlans, setItdrpPlans] = useState([]);
  
  // Selection state
  const [selectedScenarios, setSelectedScenarios] = useState([]); // multi-select
  const [selectedPlans, setSelectedPlans] = useState([]);

  // AI Scenario Matching state
  const [aiMatching, setAiMatching] = useState(false);
  const [aiResult, setAiResult] = useState(null); // {matched_plans, recommended_selection, overall_gaps, ai_source, summary, scenario}
  
  // Visualization state
  const [showVisualization, setShowVisualization] = useState(false);
  const [visualizationType, setVisualizationType] = useState("swimlane"); // swimlane, network, timeline
  
  // Drill-down state
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Aggregated tasks for visualization
  const [orchestratedTasks, setOrchestratedTasks] = useState([]);

  const isAdmin = user?.role === "bcm_manager" || user?.role === "department_head" || user?.role === "it_drp_manager" || user?.role === "admin";

  // Fetch all plans
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch BCP Plans
      const bcpResponse = await api.get("/bia-engine/plans");
      setBcpPlans((bcpResponse.data.plans || []).map(p => ({ ...p, plan_category: "bcp" })));
      
      // Fetch IT-DRP Plans from dedicated endpoint
      const itdrpResponse = await api.get("/it-drp/plans");
      setItdrpPlans((itdrpResponse.data.plans || []).map(p => ({ ...p, plan_category: "it_drp" })));
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (isAdmin) {
      fetchPlans();
    }
  }, [fetchPlans, isAdmin]);

  // Toggle plan selection
  const togglePlanSelection = (planId, planCategory) => {
    setSelectedPlans(prev => {
      const key = `${planCategory}:${planId}`;
      if (prev.includes(key)) {
        return prev.filter(p => p !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Toggle scenario selection (multi-select)
  const toggleScenario = (scenarioId) => {
    setSelectedScenarios(prev => {
      const next = prev.includes(scenarioId)
        ? prev.filter(s => s !== scenarioId)
        : [...prev, scenarioId];
      // Clear AI result when scenarios change
      setAiResult(null);
      return next;
    });
  };

  // Run AI Scenario-to-Plan matching
  const runAiMatch = useCallback(async () => {
    if (selectedScenarios.length === 0) {
      toast.error("Pick at least one disaster scenario first");
      return;
    }
    setAiMatching(true);
    try {
      const res = await api.post("/orchestration-ai/scenario-match", {
        scenario_ids: selectedScenarios,
        use_ai: true,
      });
      if (!res.data?.success) {
        toast.error(res.data?.error || "AI match failed");
        return;
      }
      setAiResult(res.data);
      // Auto-select HIGH-relevance plans
      if (Array.isArray(res.data.recommended_selection)) {
        setSelectedPlans(res.data.recommended_selection);
      }
      const src = res.data.ai_source === "gpt-4o" ? "AI" : "Rule-based";
      toast.success(`AI matched ${res.data.summary.high} HIGH / ${res.data.summary.medium} MEDIUM plans (${src})`);
    } catch (err) {
      console.error("AI scenario match failed:", err);
      toast.error("AI scenario matching failed — fallback to manual selection");
    } finally {
      setAiMatching(false);
    }
  }, [api, selectedScenarios]);

  // Build a quick lookup of AI match results by plan_id for badge rendering
  const aiMatchByPlan = useMemo(() => {
    if (!aiResult?.matched_plans) return {};
    const map = {};
    aiResult.matched_plans.forEach(m => { map[`${m.category}:${m.plan_id}`] = m; });
    return map;
  }, [aiResult]);

  // Build orchestrated task list from selected plans
  const buildOrchestratedTasks = useCallback(() => {
    const allTasks = [];
    let taskCounter = 1;
    
    selectedPlans.forEach(planKey => {
      const [category, planId] = planKey.split(":");
      const plans = category === "bcp" ? bcpPlans : itdrpPlans;
      const plan = plans.find(p => p.id === planId);
      
      if (plan?.recovery_tasks) {
        plan.recovery_tasks.forEach(task => {
          allTasks.push({
            ...task,
            global_id: `G${String(taskCounter++).padStart(3, '0')}`,
            plan_name: plan.plan_name || plan.name,
            plan_category: category,
            plan_id: planId,
          });
        });
      }
    });
    
    setOrchestratedTasks(allTasks);
  }, [selectedPlans, bcpPlans, itdrpPlans]);

  // Handle task drill-down click
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  // Export to PDF
  const handleExportPDF = async () => {
    if (selectedPlans.length === 0) {
      toast.error("No plans selected");
      return;
    }
    
    setExporting(true);
    try {
      const planIds = selectedPlans.map(key => key.split(":")[1]);
      const response = await api.post("/it-drp/orchestration/export/pdf", planIds, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Recovery_Orchestration_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  // Group tasks by owner for swimlane visualization
  const swimlaneData = useMemo(() => {
    const owners = {};
    
    orchestratedTasks.forEach(task => {
      const owner = task.task_owner || "Unassigned";
      if (!owners[owner]) {
        owners[owner] = {
          name: owner,
          tasks: [],
          colorIndex: Object.keys(owners).length % SWIMLANE_COLORS.length,
        };
      }
      owners[owner].tasks.push(task);
    });
    
    // Sort tasks within each lane by predecessor dependencies
    Object.values(owners).forEach(lane => {
      lane.tasks.sort((a, b) => {
        // Tasks with no predecessor come first
        if (!a.predecessor_task_id && b.predecessor_task_id) return -1;
        if (a.predecessor_task_id && !b.predecessor_task_id) return 1;
        // Then sort by task ID
        return a.task_id.localeCompare(b.task_id);
      });
    });
    
    return Object.values(owners);
  }, [orchestratedTasks]);

  // Calculate parallel execution phases
  const parallelPhases = useMemo(() => {
    if (orchestratedTasks.length === 0) return [];
    
    const phases = [];
    const completed = new Set();
    const remaining = [...orchestratedTasks];
    
    while (remaining.length > 0) {
      // Find tasks that can run in parallel (no predecessor or predecessor completed)
      const canRun = remaining.filter(task => {
        if (!task.predecessor_task_id) return true;
        return completed.has(task.predecessor_task_id);
      });
      
      if (canRun.length === 0) {
        // Prevent infinite loop - add remaining tasks
        phases.push({ tasks: remaining, phase: phases.length + 1 });
        break;
      }
      
      phases.push({ tasks: canRun, phase: phases.length + 1 });
      
      // Mark as completed and remove from remaining
      canRun.forEach(task => {
        completed.add(task.task_id);
        const idx = remaining.findIndex(t => t.task_id === task.task_id && t.plan_id === task.plan_id);
        if (idx !== -1) remaining.splice(idx, 1);
      });
    }
    
    return phases;
  }, [orchestratedTasks]);

  // Open visualization
  const openVisualization = async () => {
    if (selectedPlans.length === 0) {
      toast.error("Please select at least one plan");
      return;
    }

    // Hydrate recovery_tasks for BCP plans (the /bia-engine/plans list endpoint only returns tasks_count,
    // not the full recovery_tasks array). Fetch full plan details in parallel for any selected BCP
    // that doesn't yet have its tasks loaded.
    const bcpKeysNeedingHydration = selectedPlans.filter(key => {
      const [category, planId] = key.split(":");
      if (category !== "bcp") return false;
      const plan = bcpPlans.find(p => p.id === planId);
      return plan && !Array.isArray(plan.recovery_tasks);
    });

    if (bcpKeysNeedingHydration.length > 0) {
      try {
        const hydrated = await Promise.all(
          bcpKeysNeedingHydration.map(async key => {
            const planId = key.split(":")[1];
            const res = await api.get(`/bia-engine/plans/${planId}`);
            return { planId, tasks: res.data?.recovery_tasks || [] };
          })
        );
        setBcpPlans(prev =>
          prev.map(p => {
            const hit = hydrated.find(h => h.planId === p.id);
            return hit ? { ...p, recovery_tasks: hit.tasks } : p;
          })
        );
        // Build tasks using the freshly hydrated data (state update is async,
        // so pass the merged list directly).
        const mergedBcp = bcpPlans.map(p => {
          const hit = hydrated.find(h => h.planId === p.id);
          return hit ? { ...p, recovery_tasks: hit.tasks } : p;
        });
        buildOrchestratedTasksFrom(mergedBcp, itdrpPlans);
      } catch (err) {
        console.error("Failed to hydrate BCP plan tasks:", err);
        toast.error("Could not load all plan tasks — showing partial view");
        buildOrchestratedTasks();
      }
    } else {
      buildOrchestratedTasks();
    }
    setShowVisualization(true);
  };

  // Build orchestrated tasks from explicit plan arrays (used by openVisualization after hydration)
  const buildOrchestratedTasksFrom = (bcpSource, itdrpSource) => {
    const allTasks = [];
    let taskCounter = 1;

    selectedPlans.forEach(planKey => {
      const [category, planId] = planKey.split(":");
      const plans = category === "bcp" ? bcpSource : itdrpSource;
      const plan = plans.find(p => p.id === planId);

      if (plan?.recovery_tasks) {
        plan.recovery_tasks.forEach(task => {
          allTasks.push({
            ...task,
            global_id: `G${String(taskCounter++).padStart(3, "0")}`,
            plan_name: plan.plan_name || plan.name || plan.title,
            plan_category: category,
            plan_id: planId,
          });
        });
      }
    });

    setOrchestratedTasks(allTasks);
  };

  // Render swimlane visualization
  const renderSwimlaneView = () => (
    <div className="space-y-4" data-testid="swimlane-view">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Recovery Swimlane by Task Owner</h3>
        <Badge variant="outline">
          {orchestratedTasks.length} total tasks • {swimlaneData.length} owners
        </Badge>
      </div>
      
      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Timeline header */}
          <div className="flex border-b pb-2 mb-4">
            <div className="w-40 flex-shrink-0 font-semibold text-slate-600">Task Owner</div>
            <div className="flex-1 flex">
              {parallelPhases.map((phase, idx) => (
                <div key={idx} className="flex-1 text-center text-xs text-slate-500">
                  Phase {phase.phase}
                </div>
              ))}
            </div>
          </div>
          
          {/* Swimlanes */}
          {swimlaneData.map((lane, laneIdx) => {
            const colors = SWIMLANE_COLORS[lane.colorIndex];
            return (
              <div key={laneIdx} className="flex mb-2" data-testid={`swimlane-${laneIdx}`}>
                {/* Lane header */}
                <div className={`w-40 flex-shrink-0 ${colors.header} text-white px-3 py-2 rounded-l-lg flex items-center gap-2`}>
                  <Users className="w-4 h-4" />
                  <span className="font-medium text-sm truncate">{lane.name}</span>
                </div>
                
                {/* Tasks */}
                <div className={`flex-1 ${colors.bg} ${colors.border} border-2 border-l-0 rounded-r-lg p-2 flex gap-2 overflow-x-auto`}>
                  {lane.tasks.map((task, taskIdx) => (
                    <div
                      key={taskIdx}
                      className={`min-w-[180px] bg-white rounded-lg p-2 shadow-sm border ${colors.border} hover:shadow-md hover:scale-105 transition-all cursor-pointer`}
                      title="Click for details"
                      onClick={() => handleTaskClick(task)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-mono text-xs font-bold ${colors.text}`}>{task.task_id}</span>
                        <Badge variant="outline" className="text-[10px] px-1">
                          {task.estimated_duration}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-700 line-clamp-2">{task.actual_task}</p>
                      {task.predecessor_task_id && (
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-slate-500">
                          <GitBranch className="w-3 h-3" />
                          After: {task.predecessor_task_id}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-400 mt-1 truncate">
                        {task.plan_name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Render parallel phases view (network-like)
  const renderNetworkView = () => (
    <div className="space-y-4" data-testid="network-view">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Parallel Execution Phases</h3>
        <Badge variant="outline">
          {parallelPhases.length} phases • {orchestratedTasks.length} tasks
        </Badge>
      </div>
      
      <div className="space-y-6">
        {parallelPhases.map((phase, phaseIdx) => (
          <div key={phaseIdx} className="relative" data-testid={`phase-${phaseIdx}`}>
            {/* Phase connector */}
            {phaseIdx > 0 && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <ChevronRight className="w-6 h-6 text-slate-400 rotate-90" />
              </div>
            )}
            
            {/* Phase header */}
            <div className="flex items-center gap-2 mb-3">
              <div className={`px-3 py-1 rounded-full font-medium text-sm ${
                phaseIdx === 0 ? "bg-emerald-100 text-emerald-700" :
                phaseIdx === parallelPhases.length - 1 ? "bg-purple-100 text-purple-700" :
                "bg-blue-100 text-blue-700"
              }`}>
                Phase {phase.phase}
              </div>
              <span className="text-sm text-slate-500">
                {phase.tasks.length} task{phase.tasks.length !== 1 ? "s" : ""} can run in parallel
              </span>
            </div>
            
            {/* Tasks in this phase */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {phase.tasks.map((task, taskIdx) => {
                const ownerColorIdx = swimlaneData.findIndex(l => l.name === task.task_owner);
                const colors = SWIMLANE_COLORS[ownerColorIdx >= 0 ? ownerColorIdx % SWIMLANE_COLORS.length : 0];
                
                return (
                  <Card key={taskIdx} className={`${colors.border} border-l-4`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`font-mono font-bold text-sm ${colors.text}`}>{task.task_id}</span>
                        <Badge variant="outline" className="text-xs">{task.estimated_duration}</Badge>
                      </div>
                      <p className="text-sm text-slate-700 line-clamp-2 mb-2">{task.actual_task}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Users className="w-3 h-3" />
                        {task.task_owner}
                      </div>
                      {task.predecessor_task_id && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                          <GitBranch className="w-3 h-3" />
                          Depends on: {task.predecessor_task_id}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render checklist view
  const renderChecklistView = () => (
    <div className="space-y-4" data-testid="checklist-view">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Recovery Checklist</h3>
        <Badge variant="outline">
          {orchestratedTasks.length} tasks
        </Badge>
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-800 text-white">
              <TableHead className="text-white w-16">#</TableHead>
              <TableHead className="text-white w-20">Task ID</TableHead>
              <TableHead className="text-white">Task</TableHead>
              <TableHead className="text-white w-32">Owner</TableHead>
              <TableHead className="text-white w-24">Plan</TableHead>
              <TableHead className="text-white w-24">Predecessor</TableHead>
              <TableHead className="text-white w-24">Duration</TableHead>
              <TableHead className="text-white w-24">Start</TableHead>
              <TableHead className="text-white w-24">Complete</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orchestratedTasks.map((task, idx) => (
              <TableRow key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <TableCell className="font-medium">{idx + 1}</TableCell>
                <TableCell className="font-mono font-semibold text-purple-700">{task.task_id}</TableCell>
                <TableCell>{task.actual_task}</TableCell>
                <TableCell>{task.task_owner}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={task.plan_category === "bcp" ? "bg-blue-50" : "bg-purple-50"}>
                    {task.plan_category.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-slate-600">{task.predecessor_task_id || "—"}</TableCell>
                <TableCell>{task.estimated_duration}</TableCell>
                <TableCell className="text-slate-400 italic text-sm">___:___</TableCell>
                <TableCell className="text-slate-400 italic text-sm">___:___</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Restricted</h2>
        <p className="text-slate-500">Only BCM Manager and IT-DRP Manager can access the Orchestration Dashboard</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orchestration-dashboard">
      {/* Header */}
      {!embedded && (
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-purple-100 text-purple-700">Orchestration</Badge>
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">ISO 22301:8.4</Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Recovery Orchestration Dashboard</h1>
          <p className="text-slate-600">Select and visualize integrated BCP & IT-DRP recovery plans</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPlans}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={openVisualization}
            disabled={selectedPlans.length === 0}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Eye className="w-4 h-4 mr-2" />
            Visualize Recovery
          </Button>
        </div>
      </div>
      )}

      {/* V14 D4 — Hybrid app 3-role coordination workstreams (hidden if none) */}
      <HybridCoordinationRollup />

      {/* Onboarding Helper — 3-step flow (always visible, including embedded mode) */}
      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-sky-50 via-white to-purple-50 p-4" data-testid="orchestration-helper">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-600 text-white">
            <Play className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">How to run an orchestrated recovery</p>
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-slate-600">
              <div className={`flex items-center gap-1.5 ${selectedScenarios.length > 0 ? "text-emerald-700 font-medium" : ""}`}>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${selectedScenarios.length > 0 ? "bg-emerald-600" : "bg-slate-400"}`}>1</span>
                Pick one or more disaster scenarios
                {selectedScenarios.length > 0 && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 hidden sm:block" />
              <div className={`flex items-center gap-1.5 ${selectedPlans.length > 0 ? "text-emerald-700 font-medium" : ""}`}>
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${selectedPlans.length > 0 ? "bg-emerald-600" : "bg-slate-400"}`}>2</span>
                Select the plans to activate
                {selectedPlans.length > 0 && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-slate-300 hidden sm:block" />
              <div className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white bg-slate-400">3</span>
                Visualize &amp; export the activation runbook
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disaster Scenario Selector — Multi-select + AI Match */}
      <Card data-testid="scenario-selector-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Disaster Scenario(s)
                <Badge variant="outline" className="ml-2 text-[10px]">Multi-select</Badge>
              </CardTitle>
              <CardDescription>
                Pick one or more scenarios (e.g. concurrent Cyber Attack + Data Center Failure), then let AI rank your plans.
              </CardDescription>
            </div>
            <Button
              onClick={runAiMatch}
              disabled={selectedScenarios.length === 0 || aiMatching}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
              data-testid="ai-scenario-match-btn"
            >
              {aiMatching ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> AI matching…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> AI Match Plans{selectedScenarios.length > 0 ? ` (${selectedScenarios.length})` : ""}</>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {DISASTER_SCENARIOS.map((scenario) => {
              const Icon = scenario.icon;
              const isSelected = selectedScenarios.includes(scenario.id);
              return (
                <button
                  key={scenario.id}
                  onClick={() => toggleScenario(scenario.id)}
                  className={`relative p-3 rounded-lg border-2 transition-all text-left ${
                    isSelected
                      ? `${scenario.color} border-current ring-2 ring-offset-1 ring-purple-300`
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`scenario-${scenario.id}`}
                >
                  <Icon className="w-5 h-5 mb-1" />
                  <p className="text-xs font-medium line-clamp-2">{scenario.name}</p>
                  {isSelected && (
                    <CheckCircle2 className="absolute top-1.5 right-1.5 h-4 w-4 text-emerald-600 bg-white rounded-full" />
                  )}
                </button>
              );
            })}
          </div>
          {selectedScenarios.length > 0 && !aiResult && !aiMatching && (
            <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-purple-500" />
              <span>
                <strong>{selectedScenarios.length}</strong> scenario(s) selected —
                click <strong>AI Match Plans</strong> to rank plans by relevance, auto-select HIGH matches, and surface coverage gaps.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Scenario Match Result Banner */}
      {aiResult && (
        <Card className="border-purple-300 bg-gradient-to-br from-purple-50 via-white to-sky-50" data-testid="ai-match-result-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Relevance Analysis
                  <Badge variant="outline" className="text-[10px] bg-white">
                    {aiResult.ai_source === "gpt-4o" ? "AI" : "Rule-based"}
                  </Badge>
                </CardTitle>
                <CardDescription className="mt-1">
                  Scenario: <strong>{aiResult.scenario.combined_name}</strong> · Peak severity:{" "}
                  <strong className="uppercase">{aiResult.scenario.peak_severity}</strong>
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setAiResult(null)} className="text-slate-500">
                Clear
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className="rounded-lg bg-red-50 border border-red-200 p-2 text-center">
                <div className="text-2xl font-bold text-red-700">{aiResult.summary.high}</div>
                <div className="text-[11px] text-red-600">HIGH Relevance</div>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-center">
                <div className="text-2xl font-bold text-amber-700">{aiResult.summary.medium}</div>
                <div className="text-[11px] text-amber-600">MEDIUM</div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-2 text-center">
                <div className="text-2xl font-bold text-slate-600">{aiResult.summary.low}</div>
                <div className="text-[11px] text-slate-500">LOW</div>
              </div>
              <div className="rounded-lg bg-slate-100 border border-slate-200 p-2 text-center">
                <div className="text-2xl font-bold text-slate-500">{aiResult.summary.not_applicable}</div>
                <div className="text-[11px] text-slate-500">Not Applicable</div>
              </div>
            </div>
            {aiResult.overall_gaps?.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="ai-overall-gaps">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-amber-900 mb-1">Coverage gaps AI identified across your plans:</p>
                    <ul className="text-xs text-amber-800 list-disc pl-4 space-y-0.5">
                      {aiResult.overall_gaps.map((g, i) => <li key={i}>{g}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <p className="mt-3 text-[11px] text-slate-500">
              Plans are now sorted by relevance. Checkboxes auto-selected the {aiResult.summary.high} HIGH-relevance plan(s) —
              adjust as needed, then click <strong>Visualize &amp; Activate</strong>.
            </p>
          </CardContent>
        </Card>
      )}


      {/* Plans Selection */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="plans" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            All Plans
          </TabsTrigger>
          <TabsTrigger value="bcp" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            BCP Plans ({bcpPlans.length})
          </TabsTrigger>
          <TabsTrigger value="itdrp" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            IT-DRP Plans ({itdrpPlans.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* BCP Plans */}
            <Card>
              <CardHeader className="pb-3 bg-blue-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Business Continuity Plans
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 max-h-80 overflow-y-auto">
                {bcpPlans.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No BCP plans found</p>
                ) : (
                  <div className="space-y-2">
                    {[...bcpPlans].sort((a, b) => {
                      const ma = aiMatchByPlan[`bcp:${a.id}`];
                      const mb = aiMatchByPlan[`bcp:${b.id}`];
                      const rank = { HIGH: 3, MEDIUM: 2, LOW: 1, NOT_APPLICABLE: 0 };
                      return (rank[mb?.relevance] || 0) - (rank[ma?.relevance] || 0);
                    }).map((plan) => {
                      const match = aiMatchByPlan[`bcp:${plan.id}`];
                      return (
                      <div
                        key={plan.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedPlans.includes(`bcp:${plan.id}`)
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 hover:border-blue-300"
                        }`}
                        onClick={() => togglePlanSelection(plan.id, "bcp")}
                        data-testid={`plan-bcp-${plan.id}`}
                      >
                        <Checkbox
                          checked={selectedPlans.includes(`bcp:${plan.id}`)}
                          className="border-blue-500 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium truncate">{plan.name || plan.plan_name || plan.title || "Untitled BCP Plan"}</p>
                            {match && <RelevanceBadge match={match} />}
                          </div>
                          <p className="text-xs text-slate-500">
                            {plan.recovery_tasks?.length ?? plan.tasks_count ?? 0} tasks • {plan.status || "draft"}
                          </p>
                          {match?.reasoning && (
                            <p className="text-[11px] text-slate-600 mt-1 italic line-clamp-2">
                              <Sparkles className="inline w-3 h-3 mr-0.5 text-purple-500" />
                              {match.reasoning}
                            </p>
                          )}
                          {match?.gaps?.length > 0 && (
                            <p className="text-[11px] text-amber-700 mt-1">
                              <AlertCircle className="inline w-3 h-3 mr-0.5" />
                              Gap: {match.gaps.join(" · ")}
                            </p>
                          )}
                        </div>
                      </div>
                    );})}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* IT-DRP Plans */}
            <Card>
              <CardHeader className="pb-3 bg-purple-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Server className="w-5 h-5 text-purple-600" />
                  IT Disaster Recovery Plans
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 max-h-80 overflow-y-auto">
                {itdrpPlans.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No IT-DRP plans found</p>
                ) : (
                  <div className="space-y-2">
                    {[...itdrpPlans].sort((a, b) => {
                      const ma = aiMatchByPlan[`it_drp:${a.id}`];
                      const mb = aiMatchByPlan[`it_drp:${b.id}`];
                      const rank = { HIGH: 3, MEDIUM: 2, LOW: 1, NOT_APPLICABLE: 0 };
                      return (rank[mb?.relevance] || 0) - (rank[ma?.relevance] || 0);
                    }).map((plan) => {
                      const match = aiMatchByPlan[`it_drp:${plan.id}`];
                      return (
                      <div
                        key={plan.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedPlans.includes(`it_drp:${plan.id}`)
                            ? "border-purple-500 bg-purple-50"
                            : "border-slate-200 hover:border-purple-300"
                        }`}
                        onClick={() => togglePlanSelection(plan.id, "it_drp")}
                        data-testid={`plan-itdrp-${plan.id}`}
                      >
                        <Checkbox
                          checked={selectedPlans.includes(`it_drp:${plan.id}`)}
                          className="border-purple-500 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium truncate">{plan.plan_name || plan.name || plan.title || "Untitled IT-DRP Plan"}</p>
                            {match && <RelevanceBadge match={match} />}
                          </div>
                          <p className="text-xs text-slate-500">
                            {plan.recovery_tasks?.length ?? plan.tasks_count ?? 0} tasks • {plan.status || "draft"}
                          </p>
                          {match?.reasoning && (
                            <p className="text-[11px] text-slate-600 mt-1 italic line-clamp-2">
                              <Sparkles className="inline w-3 h-3 mr-0.5 text-purple-500" />
                              {match.reasoning}
                            </p>
                          )}
                          {match?.gaps?.length > 0 && (
                            <p className="text-[11px] text-amber-700 mt-1">
                              <AlertCircle className="inline w-3 h-3 mr-0.5" />
                              Gap: {match.gaps.join(" · ")}
                            </p>
                          )}
                        </div>
                      </div>
                    );})}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="bcp">
          {/* BCP-only view - similar structure */}
        </TabsContent>

        <TabsContent value="itdrp">
          {/* IT-DRP-only view - similar structure */}
        </TabsContent>
      </Tabs>

      {/* Selection Summary + CTA (visible in both standalone & embedded modes) */}
      {selectedPlans.length > 0 ? (
        <Card className="border-purple-300 bg-gradient-to-r from-purple-50 to-sky-50 sticky bottom-4 shadow-lg" data-testid="orchestration-cta-banner">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-slate-800">
                  {selectedPlans.length} plan(s) selected — ready to orchestrate
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedPlans([])} data-testid="orchestration-clear-btn">
                  Clear Selection
                </Button>
                <Button
                  onClick={openVisualization}
                  className="bg-purple-600 hover:bg-purple-700"
                  size="sm"
                  data-testid="orchestration-visualize-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Visualize &amp; Activate ({selectedPlans.length})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPDF}
                  data-testid="orchestration-export-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Runbook PDF
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-dashed border-slate-300 bg-slate-50/50" data-testid="orchestration-empty-banner">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span>
                <strong>Next step:</strong> tick at least one plan above to unlock <em>Visualize &amp; Activate</em> and the runbook PDF export.
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visualization Dialog */}
      <Dialog open={showVisualization} onOpenChange={setShowVisualization}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Network className="w-5 h-5 text-purple-600" />
              Recovery Orchestration Visualization
            </DialogTitle>
            <DialogDescription>
              Integrated view of {selectedPlans.length} recovery plan(s) with {orchestratedTasks.length} total tasks
            </DialogDescription>
          </DialogHeader>
          
          {/* View Type Selector */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={visualizationType === "swimlane" ? "default" : "outline"}
              size="sm"
              onClick={() => setVisualizationType("swimlane")}
              className={visualizationType === "swimlane" ? "bg-purple-600" : ""}
            >
              <Users className="w-4 h-4 mr-2" />
              Swimlane
            </Button>
            <Button
              variant={visualizationType === "network" ? "default" : "outline"}
              size="sm"
              onClick={() => setVisualizationType("network")}
              className={visualizationType === "network" ? "bg-purple-600" : ""}
            >
              <GitBranch className="w-4 h-4 mr-2" />
              Parallel Phases
            </Button>
            <Button
              variant={visualizationType === "checklist" ? "default" : "outline"}
              size="sm"
              onClick={() => setVisualizationType("checklist")}
              className={visualizationType === "checklist" ? "bg-purple-600" : ""}
            >
              <LayoutList className="w-4 h-4 mr-2" />
              Checklist
            </Button>
          </div>
          
          {/* Visualization Content */}
          {visualizationType === "swimlane" && renderSwimlaneView()}
          {visualizationType === "network" && renderNetworkView()}
          {visualizationType === "checklist" && renderChecklistView()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVisualization(false)}>
              Close
            </Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleExportPDF}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export as PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Drill-Down Dialog */}
      <Dialog open={showTaskDetail} onOpenChange={setShowTaskDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-600" />
              Task Details
            </DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-bold text-purple-700 text-lg">{selectedTask.task_id}</span>
                  <Badge className="bg-purple-100 text-purple-700">
                    {selectedTask.plan_category?.toUpperCase() || "BCP"}
                  </Badge>
                </div>
                <p className="text-slate-700">{selectedTask.actual_task}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Task Owner</Label>
                  <p className="font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    {selectedTask.task_owner}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Estimated Duration</Label>
                  <p className="font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    {selectedTask.estimated_duration}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Predecessor Task</Label>
                  <p className="font-medium flex items-center gap-2">
                    <GitBranch className="w-4 h-4 text-slate-400" />
                    {selectedTask.predecessor_task_id || "None (Start Task)"}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Source Plan</Label>
                  <p className="font-medium truncate">{selectedTask.plan_name}</p>
                </div>
              </div>
              
              {selectedTask.notes && (
                <div>
                  <Label className="text-slate-500">Notes</Label>
                  <p className="text-slate-700 mt-1">{selectedTask.notes}</p>
                </div>
              )}
              
              <div className="pt-4 border-t">
                <Label className="text-slate-500">Exercise Tracking</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Start Time</p>
                    <p className="font-mono text-lg">{selectedTask.start_time || "___:___"}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Completion Time</p>
                    <p className="font-mono text-lg">{selectedTask.completion_time || "___:___"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDetail(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// AI Relevance Badge — small colored pill next to plan titles
function RelevanceBadge({ match }) {
  const cfg = {
    HIGH:           { bg: "bg-red-100 text-red-700 border-red-200",         label: "HIGH" },
    MEDIUM:         { bg: "bg-amber-100 text-amber-700 border-amber-200",   label: "MEDIUM" },
    LOW:            { bg: "bg-slate-100 text-slate-600 border-slate-200",   label: "LOW" },
    NOT_APPLICABLE: { bg: "bg-slate-200 text-slate-500 border-slate-300 line-through", label: "N/A" },
  }[match.relevance] || { bg: "bg-slate-100 text-slate-600", label: match.relevance };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold shrink-0 ${cfg.bg}`}
      title={`${match.relevance} · confidence ${Math.round((match.confidence || 0) * 100)}%`}
      data-testid={`relevance-badge-${match.relevance}`}
    >
      <Sparkles className="w-2.5 h-2.5" />
      {cfg.label}
      <span className="opacity-60 ml-0.5">{Math.round((match.confidence || 0) * 100)}%</span>
    </span>
  );
}

